import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../../../api'
import { baseUrl } from '../../../Environments/environment'
import { toast } from 'react-toastify'
import PaymentModal from './PaymentModal'
import { formatShopAmount } from '../../CreditSetup/paymentCurrency.util'

const PurchaseModalSkeleton = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="h-6 w-40 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
        </div>

        {/* Items List Skeleton */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg animate-pulse"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-5 w-32 bg-gray-200 rounded"></div>
                    <div className="flex items-center gap-4">
                      <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
                <div className="h-6 w-20 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
          
          {/* Address Module Skeleton */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="h-5 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-16 bg-gray-200 rounded"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Skeleton */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-12 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  )
}

const PurchaseModal = ({ propertyId,isOpen, onClose, selectedItems, quantities, items, totalPrice, onOrderSuccess }) => {
  const [loading, setLoading] = useState(true)
  const [propertyDetails, setPropertyDetails] = useState(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [address, setAddress] = useState({
    addressLine1: '',
    city: '',
    zipcode: '',
    country: ''
  })

  const getQuantity = (itemId) => {
    return quantities[itemId] || 1
  }

  const getSelectedItemsList = () => {
    return Array.from(selectedItems).map((itemId) => {
      const item = items.find((i) => i.id === itemId)
      const quantity = getQuantity(itemId)
      return {
        ...item,
        quantity,
        subtotal: item.price * quantity
      }
    })
  }

  const getPropertyDetails = async () => {
    try {
       propertyId = propertyId||localStorage.getItem("selectedPropertyId");
      const response = await apiClient.get(
        `${baseUrl}/property/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (response.status === 200) {
        console.log("Property details fetched successfully", response.data.result);
        return response.data.result.property;
      }
      return null;
    } catch (error) {
      console.error("Error fetching property details:", error);
      return null;
    }
  }

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      getPropertyDetails().then((details) => {
        setPropertyDetails(details)
        // Pre-fill address from property details if available
        if (details?.property?.address) {
          setAddress({
            addressLine1: details.property.address || '',
            city: details.property.city || '',
            zipcode: details.property.zipcode || '',
            country: details.property.country || ''
          })
        }
        setLoading(false)
      })
    } else {
      // Reset when modal closes
      setPropertyDetails(null)
      setLoading(true)
      setAddress({
        addressLine1: '',
        city: '',
        zipcode: '',
        country: ''
      })
    }
  }, [isOpen])

  const handleAddressChange = (field, value) => {
    setAddress((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePurchaseClick = () => {
    // Validate address before opening payment modal
    if (!address.addressLine1 || !address.city || !address.zipcode || !address.country) {
      toast.error("Please fill in all address fields")
      return
    }
    setIsPaymentModalOpen(true)
  }

  const handlePaymentSuccess = async () => {
    // Order creation is now handled in PaymentModal
    setIsPaymentModalOpen(false)
    // Clear selected items after successful order
    if (onOrderSuccess) {
      await onOrderSuccess()
    }
    onClose()
  }

  // Prepare order data to pass to PaymentModal
  const getOrderData = () => {
    const selectedItemsList = getSelectedItemsList()
    const orderCurrency = selectedItemsList[0]?.currency ?? "usd"
    const orderItems = selectedItemsList.map((item) => ({
      itemId: item.id,
      itemName: item.name,
      itemQuantity: item.quantity,
      itemPrice: item.price,
      itemTotal: item.subtotal
    }))

    return {
      orderStatus: "pending",
      orderAmount: totalPrice,
      currency: orderCurrency,
      orderItems: orderItems,
      shippingAddress: {
        addressLine1: address.addressLine1,
        city: address.city,
        zipcode: address.zipcode,
        country: address.country
      }
    }
  }

  if (!isOpen) return null

  if (loading || !propertyDetails) {
    return <PurchaseModalSkeleton />
  }

  const selectedItemsList = getSelectedItemsList()
  const currency = selectedItemsList[0]?.currency ?? "usd"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Purchase Summary</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {selectedItemsList.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900 mb-1">
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>Quantity: {item.quantity}</span>
                      <span>Price: {formatShopAmount(item.price, item.currency)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-medium text-blue-600">
                    {formatShopAmount(item.subtotal, item.currency)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Address Module */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               <div className="sm:col-span-2">
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Address
                 </label>
                 <input
                   type="text"
                   value={address.addressLine1}
                   onChange={(e) => handleAddressChange('addressLine1', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   placeholder="Enter your address"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   City
                 </label>
                 <input
                   type="text"
                   value={address.city}
                   onChange={(e) => handleAddressChange('city', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   placeholder="Enter city"
                 />
               </div>
    <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Pincode
                 </label>
                 <input
                   type="text"
                   value={address.zipcode}
                   onChange={(e) => handleAddressChange('zipcode', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   placeholder="Enter pincode"
                 />
               </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter country"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
    <div>
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatShopAmount(totalPrice, currency)}
              </p>
            </div>
             <button
               onClick={handlePurchaseClick}
               className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
             >
               Purchase
             </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        propertyId={propertyId}
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        totalAmount={totalPrice}
        orderData={getOrderData()}
        onPaymentSuccess={handlePaymentSuccess}
        onError={() => {
          // Close PaymentModal
          setIsPaymentModalOpen(false)
          // Close PurchaseModal
          onClose()
        }}
      />
    </div>
  )
}

export default PurchaseModal
